import { useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import socketService from '@business/services/SocketService';
import { incrementUnread } from '@ports/common/notificationStateSlice';
import { notificationInjectedApi } from '@ports/internal-services/notification/notificationSlice';
import { NotificationToast } from '@business/workflows/ComponentUI/Notification/NotificationToast';
import { routePaths } from '@utils/constants/routePaths';

const SERVICE_ID = 'notificationBell';

/**
 * Subscribes to the shared `app.event` socket channel and reacts only to
 * user-facing notification events (share / contribution), discriminated by
 * `payload.kind === 'userNotification'`. Uses a distinct serviceId so it
 * coexists with the existing MediaUploadPersister ('onMessage') subscription.
 *
 * On a notification event it bumps the header badge, invalidates the
 * notification queries (so the bell dropdown / list refetch), and shows a
 * card-style toast matching the upload-progress toast design.
 */
export const useNotificationSocket = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const onSocketMessageReceived = useCallback(
    (data: any) => {
      const payload = data?.payload;
      if (payload?.kind !== 'userNotification') return;

      dispatch(incrementUnread());
      dispatch(notificationInjectedApi.util.invalidateTags(['Notifications']));

      const title = payload?.title;
      // `body`/`message` carry the descriptive, item-specific text
      // (e.g. "Alice shared 10 items with you.").
      const body =
        payload?.body ||
        payload?.message ||
        payload?.title ||
        'You have a new notification';
      const pageUrl = payload?.pageUrl || routePaths.Sharing;

      toast.success(
        <NotificationToast
          title={title}
          body={body}
          profileImage={payload?.profileImage}
          actorName={payload?.actorName}
          onClick={() => navigate(pageUrl)}
        />,
        {
          closeOnClick: true,
          hideProgressBar: true,
          icon: false,
          style: {
            padding: 0,
            background: 'transparent'
          }
        }
      );
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    socketService.subscribe(SERVICE_ID, 'app.event', onSocketMessageReceived);
    return () => {
      socketService.unsubscribe(SERVICE_ID, 'app.event');
    };
  }, [onSocketMessageReceived]);
};
